"use client";

import Link from "next/link";

import { Tooltip } from "../../../_ui/Tooltip";

/*
 * `simple` 스킨의 부품 — 시안(`docs/design/simple-skin/sample/*.html`)의 반복 조각을 그대로 옮긴 것.
 *
 * 🔴 **모서리는 전부 토큰입니다**(`--tnf-radius-*` — `../tokens.css` 의 배수표). 숫자를 적지 않습니다.
 *    원·알약(`rounded-full`)만 예외입니다(README §4 «원은 따라가지 않습니다»).
 * 🔴 **브랜드색(`accent`)은 주 버튼과 선택 상태에만** 씁니다(README §4). 링크 글자·제목·헤더에
 *    쓰지 않습니다 — 관리자가 밝은 색을 골라도 화면이 무너지지 않게.
 * 🔴 **버튼마다 툴팁** (오너 결정 2026-09-07 #4) — `Action` 이 `tooltip` 을 필수로 받습니다.
 */

/* ── 틀 ─────────────────────────────────────────────────────────────────────── */

/** 흰 카드 — 시안의 `section` (모서리 card · 안쪽 18 · 그림자). `tight` 는 목록 카드(안쪽 10). */
export function Card({ children, tight = false, className = "", as: Tag = "section", ...rest }: {
  children: React.ReactNode;
  tight?: boolean;
  className?: string;
  as?: "section" | "div";
} & Omit<React.HTMLAttributes<HTMLElement>, "className" | "children">) {
  return (
    <Tag
      {...rest}
      className={`rounded-[var(--tnf-radius-card)] bg-surface shadow-[var(--tnf-shadow-card)] ${tight ? "p-2.5" : "p-[18px]"} ${className}`}
    >
      {children}
    </Tag>
  );
}

/** 카드 안의 입력 슬롯 — 옅은 회색 칸(모서리 md). */
export function Slot({ label, labelFor, children, className = "" }: {
  label: React.ReactNode;
  /** 라벨이 가리킬 입력칸 id — 있으면 `<label>` 로 그립니다 */
  labelFor?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-[var(--tnf-radius-md)] bg-raised px-4 pt-3.5 pb-4 ${className}`}>
      {labelFor ? (
        <label htmlFor={labelFor} className="block text-[length:calc(var(--tnf-text-base)*13.5/14)] font-medium text-ink-faint">{label}</label>
      ) : (
        <div className="text-[length:calc(var(--tnf-text-base)*13.5/14)] font-medium text-ink-faint">{label}</div>
      )}
      {children}
    </div>
  );
}

/** 화면 제목 — 모바일 22 · PC 26. */
export function PageTitle({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <h1 className={`mx-0.5 mb-4 text-title font-bold tracking-[-0.03em] text-ink lg:mb-[18px] lg:text-[length:calc(var(--tnf-text-base)*26/14)] ${className}`}>{children}</h1>;
}

/** 목록 카드 위의 작은 제목 (시안 03 «자주 산 상품권» · 11 «지난 정산»). */
export function SectionTitle({ children, small = false, className = "" }: { children: React.ReactNode; small?: boolean; className?: string }) {
  return small ? (
    <h2 className={`mx-0.5 mb-[9px] text-[length:calc(var(--tnf-text-base)*13.5/14)] font-bold text-ink-faint ${className}`}>{children}</h2>
  ) : (
    <h2 className={`mx-0.5 mt-[22px] mb-3 text-subtitle font-bold tracking-[-0.02em] text-ink ${className}`}>{children}</h2>
  );
}

/** 뒤로 — 시안 12·13 의 «‹ 정산». */
export function BackLink({ href, children, tooltip }: { href: string; children: React.ReactNode; tooltip: string }) {
  return (
    <Tooltip text={tooltip} placement="bottom" className="mx-0.5 mb-2.5">
      {(trigger) => (
        <Link href={href} {...trigger} className="inline-flex items-center gap-[5px] text-[length:calc(var(--tnf-text-base)*13.5/14)] font-medium text-ink-soft hover:text-ink">
          <IconChevron dir="left" size={16} />
          {children}
        </Link>
      )}
    </Tooltip>
  );
}

/** 카드 아래 고지 한 줄 (README §7 «남긴 설명은 넷뿐»). */
export function Note({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={`mx-0.5 mt-4 flex gap-2 text-[length:calc(var(--tnf-text-base)*13.5/14)] leading-relaxed text-ink-faint ${className}`}>
      <span aria-hidden="true">·</span>
      <span>{children}</span>
    </p>
  );
}

/** 이름 ↔ 값 한 줄 (시안 05·07·12). */
export function InfoRow({ label, children, strong = false, divider = false }: {
  label: string;
  children: React.ReactNode;
  strong?: boolean;
  divider?: boolean;
}) {
  return (
    <div className={`flex items-baseline justify-between gap-3.5 ${divider ? "border-b border-line pb-[11px]" : ""}`}>
      <dt className="text-[length:calc(var(--tnf-text-base)*14.5/14)] text-ink-faint">{label}</dt>
      <dd className={`text-right font-bold tabular-nums text-ink ${strong ? "text-[length:calc(var(--tnf-text-base)*18/14)]" : "text-lead"}`}>{children}</dd>
    </div>
  );
}

/* ── 버튼 ───────────────────────────────────────────────────────────────────── */

type ActionVariant = "primary" | "secondary" | "danger";
type ActionCommon = {
  /** 🔴 필수. 무슨 일이 일어나는지 한 문장으로 */
  tooltip: string;
  children: React.ReactNode;
  variant?: ActionVariant;
  /** 주 버튼 58 · 부 버튼 56 · 카드 안 54 · 작은 38 (README §4) */
  size?: "xl" | "lg" | "md" | "sm";
  className?: string;
  tipBelow?: boolean;
};
type ActionButton = ActionCommon & { href?: undefined; onClick?: () => void; type?: "button" | "submit"; disabled?: boolean; loading?: boolean; loadingText?: string };
type ActionLink = ActionCommon & { href: string; replace?: boolean };

const ACTION_SIZE = {
  xl: "h-[58px] text-subtitle",
  lg: "h-14 text-[length:calc(var(--tnf-text-base)*16.5/14)]",
  md: "h-[54px] text-[length:calc(var(--tnf-text-base)*16/14)]/[1.5]",
  sm: "h-[38px] px-3.5 text-body",
} as const;

const ACTION_VARIANT: Record<ActionVariant, string> = {
  primary: "bg-accent text-on-accent hover:bg-accent-strong",
  secondary: "bg-surface text-ink-soft shadow-[inset_0_0_0_1px_var(--tnf-line)] hover:text-ink",
  danger: "bg-surface text-danger shadow-[inset_0_0_0_1px_var(--tnf-danger-border)] hover:text-[var(--tnf-danger-strong)]",
};

/** 시안의 버튼 — 모서리 md. 링크면 `href`(앱 안 이동은 `Link`), 아니면 `onClick`. */
export function Action(props: ActionButton | ActionLink) {
  const { tooltip, children, variant = "primary", size = "lg", className = "", tipBelow = false } = props;
  const shape = `inline-flex w-full items-center justify-center gap-1.5 rounded-[var(--tnf-radius-md)] px-5 font-bold tracking-[-0.01em] transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${ACTION_SIZE[size]} ${ACTION_VARIANT[variant]} ${className}`;

  return (
    <Tooltip text={tooltip} placement={tipBelow ? "bottom" : "top"} className="flex w-full">
      {(trigger) => {
        if (props.href !== undefined) {
          return (
            <Link href={props.href} replace={props.replace} className={shape} {...trigger}>
              {children}
            </Link>
          );
        }
        const busy = props.loading === true;
        return (
          <button
            type={props.type ?? "button"}
            onClick={props.onClick}
            disabled={props.disabled === true || busy}
            aria-busy={busy || undefined}
            className={shape}
            {...trigger}
          >
            {busy ? (props.loadingText ?? "잠시만 기다려 주세요") : children}
          </button>
        );
      }}
    </Tooltip>
  );
}

/* ── 표시 ───────────────────────────────────────────────────────────────────── */

/** 배지 — 사용 가능(good) · 사용 완료·취소(mute) · 진행(warn). 모서리 xxs · 높이 26. */
export function Badge({ tone, children }: { tone: "good" | "mute" | "warn" | "danger"; children: React.ReactNode }) {
  const color = {
    good: "bg-good-wash text-good",
    mute: "bg-neutral-wash text-neutral",
    warn: "bg-warn-wash text-warn",
    danger: "bg-danger-wash text-danger",
  }[tone];
  return (
    <span className={`inline-flex h-[26px] flex-none items-center rounded-[var(--tnf-radius-xxs)] px-[9px] text-[length:calc(var(--tnf-text-base)*12.5/14)] font-bold whitespace-nowrap ${color}`}>
      {children}
    </span>
  );
}

/** 쿠폰 상태 → 배지. 🔑 뜻은 글자가 말하고 색은 거듭니다. */
export function CouponBadge({ status, label }: { status: string; label?: string }) {
  const text = label ?? ({ issued: "사용 가능", used: "사용 완료", expired: "기간 만료", cancelled: "취소됨" } as Record<string, string>)[status] ?? status;
  return <Badge tone={status === "issued" ? "good" : "mute"}>{text}</Badge>;
}

/** 주문 상태 → 배지 색. 🔑 `issue_failed` 는 빨강이 아니라 진행(warn) — 관리자가 재시도하면 발급됩니다. */
export function orderTone(status: string): "good" | "mute" | "warn" {
  return status === "issued" ? "good" : status === "cancelled" || status === "refunded" ? "mute" : "warn";
}

/** 금액 — 숫자 굵게 · 「원」 한 단 작게. */
export function Won({ amount, unitClassName = "text-[0.62em] font-medium text-ink-soft", className = "" }: {
  amount: number;
  unitClassName?: string;
  className?: string;
}) {
  return (
    <span className={`font-bold tabular-nums whitespace-nowrap ${amount < 0 ? "text-danger" : ""} ${className}`}>
      {new Intl.NumberFormat("ko-KR").format(Math.trunc(amount))}
      <span className={`ml-0.5 ${unitClassName}`}>원</span>
    </span>
  );
}

/** 두 칸 전환 — 시안 08 · 13 의 `role="tablist"` (모서리 sm / 안쪽 xs). */
export function Segmented({ items, label }: {
  label: string;
  items: { key: string; label: React.ReactNode; selected: boolean; href?: string; onSelect?: () => void; tooltip: string }[];
}) {
  return (
    <div role="tablist" aria-label={label} className="mb-3.5 grid grid-cols-2 gap-1 rounded-[var(--tnf-radius-sm)] bg-raised p-1">
      {items.map((item) => {
        const cls = `flex h-[42px] w-full items-center justify-center rounded-[var(--tnf-radius-xs)] text-[length:calc(var(--tnf-text-base)*14.5/14)] ${item.selected ? "bg-surface font-bold text-ink shadow-[0_1px_3px_rgb(25_31_40_/_10%)]" : "font-medium text-ink-soft hover:text-ink"}`;
        return (
          <Tooltip key={item.key} text={item.tooltip} placement="bottom" className="flex w-full">
            {(trigger) =>
              item.href ? (
                <Link href={item.href} replace role="tab" aria-selected={item.selected} className={cls} {...trigger}>
                  {item.label}
                </Link>
              ) : (
                <button type="button" role="tab" aria-selected={item.selected} onClick={item.onSelect} className={cls} {...trigger}>
                  {item.label}
                </button>
              )
            }
          </Tooltip>
        );
      })}
    </div>
  );
}

/** 불러오는 중 — 글자로 말합니다(`_ui/Feedback` 의 `Loading` 과 같은 규칙, 카드 안 크기). */
export function Waiting({ label = "불러오는 중입니다" }: { label?: string }) {
  return (
    <p role="status" aria-live="polite" className="flex items-center justify-center gap-2.5 py-12 text-body font-medium text-ink-soft">
      <span aria-hidden="true" className="size-5 animate-spin rounded-full border-[3px] border-line border-t-accent" />
      {label}
    </p>
  );
}

/** 오류 한 줄 — 카드 안. */
export function Problem({ message, action }: { message: string; action?: React.ReactNode }) {
  if (!message) return null;
  return (
    <div role="alert" className="rounded-[var(--tnf-radius-md)] bg-danger-wash px-4 py-3.5 text-body font-bold text-danger">
      <p>{message}</p>
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

/** 빈 목록. */
export function Empty({ children, action }: { children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="px-3 py-10 text-center">
      <p className="text-lead font-medium text-ink-soft">{children}</p>
      {action && <div className="mx-auto mt-4 max-w-[240px]">{action}</div>}
    </div>
  );
}

/* ── 아이콘 ─────────────────────────────────────────────────────────────────── */

export function IconChevron({ dir = "down", size = 20, className = "" }: { dir?: "down" | "up" | "left" | "right"; size?: number; className?: string }) {
  const d = { down: "m6 9 6 6 6-6", up: "m6 15 6-6 6 6", left: "m15 5-7 7 7 7", right: "m9 5 7 7-7 7" }[dir];
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={`flex-none ${className}`}>
      <path d={d} />
    </svg>
  );
}

export function IconClose({ size = 16 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" aria-hidden="true">
      <path d="M5 5l14 14M19 5L5 19" />
    </svg>
  );
}

export function IconCheck({ size = 14, width = 3 }: { size?: number; width?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </svg>
  );
}
